import { ECreateQuestionMode } from 'components/Questionsv2/builder/context/create-question.context';
import { useMemo } from 'react';
import { useAppSelector } from 'rtk/query/hooks';
import { toast } from 'sonner';
import type { TIsDefaultValue } from 'types/questions/question.types.v4';
import { EFormType, type TFormSubType } from '@hubertai/screening-question-types';
import { EQuestionType } from '@hubertai/screening-question-types';
import { selectQuestionLanguage, selectQuestionMode } from 'rtk/questionEditorSelector';
import { useTemplateId } from 'hooks/useTemplate';
import { getDefaultFormValues } from './getDefaultFormValues';
import { getFormValue } from './getFormValue';
import { useQuestionEditorContext } from 'components/Questionsv2/builder/context/create-question.context';

export type TFormValuesResult = {
  isEnabled: TIsDefaultValue;
  isMandatory: TIsDefaultValue;
  label: TIsDefaultValue;
  labelLeft: TIsDefaultValue;
  labelRight: TIsDefaultValue;
  unit: TIsDefaultValue;
  min: TIsDefaultValue;
  max: TIsDefaultValue;
  step: TIsDefaultValue;
  placeholder: TIsDefaultValue;
  helpText: TIsDefaultValue;
};

export function useFormValues(form: TFormSubType, nodeId: string, formId: string): TFormValuesResult {
  const { questionId } = useQuestionEditorContext();
  const mode = useAppSelector((state) => selectQuestionMode(state, questionId));
  const templateId = useTemplateId();
  const questionLanguage = useAppSelector((state) => selectQuestionLanguage(state, questionId));
  const type = useAppSelector((state) => state.questionEditor.byId[questionId].type);
  const nodeParams = useAppSelector((state) => state.Template.present!.c!.parameters[nodeId]);

  const formValues = useMemo(() => {
    if (!nodeId || !questionLanguage) {
      console.log('useFormValues !nodeId || !language', nodeId, questionLanguage);
      toast.error(`useFormValues !nodeId || !language, ${nodeId}, ${questionLanguage}`);
      return null;
    }

    if (type !== EQuestionType.Form) {
      console.error('useFormValues: Erroneous question type', type);
      toast.error('useFormValues: Erroneous question type');
      return null;
    }

    if (!form || !form.id) {
      toast.error('useFormValues: form not found.');
      return null;
    }

    if (templateId && mode === ECreateQuestionMode.InTemplateEdit) {
      let formParams = nodeParams?.[formId as keyof typeof nodeParams];
      if (!formParams) {
        formParams = {};
      }

      const values = {
        isEnabled: getFormValue(nodeId, formParams, questionLanguage, 'enabled', form),
        isMandatory: getFormValue(nodeId, formParams, questionLanguage, 'is_mandatory', form),
        label: getFormValue(nodeId, formParams, questionLanguage, 'label', form),
        labelLeft: { isDefault: true, value: '' } as TIsDefaultValue,
        labelRight: { isDefault: true, value: '' } as TIsDefaultValue,
        unit: { isDefault: true, value: '' } as TIsDefaultValue,
        placeholder: { isDefault: true, value: '' } as TIsDefaultValue,
        helpText: { isDefault: true, value: '' } as TIsDefaultValue,
        min: { isDefault: true, value: 0 } as TIsDefaultValue,
        max: { isDefault: true, value: 10 } as TIsDefaultValue,
        step: { isDefault: true, value: 1 } as TIsDefaultValue,
      } satisfies TFormValuesResult;

      switch (form.type) {
        case EFormType.Phone:
        case EFormType.Text:
        case EFormType.Email:
          values.placeholder = getFormValue(nodeId, formParams, questionLanguage, 'placeholder', form);
          break;
        case EFormType.Number:
          values.min = getFormValue(nodeId, formParams, questionLanguage, 'min', form);
          values.max = getFormValue(nodeId, formParams, questionLanguage, 'max', form);
          values.step = getFormValue(nodeId, formParams, questionLanguage, 'step', form);
          values.placeholder = getFormValue(nodeId, formParams, questionLanguage, 'placeholder', form);
          break;
        case EFormType.Slider:
        case EFormType.Range:
          values.min = getFormValue(nodeId, formParams, questionLanguage, 'min', form);
          values.max = getFormValue(nodeId, formParams, questionLanguage, 'max', form);
          values.step = getFormValue(nodeId, formParams, questionLanguage, 'step', form);
          values.labelLeft = getFormValue(nodeId, formParams, questionLanguage, 'label_left', form);
          values.labelRight = getFormValue(nodeId, formParams, questionLanguage, 'label_right', form);
          values.unit = getFormValue(nodeId, formParams, questionLanguage, 'unit', form);
          break;
        case EFormType.Matrix:
          values.helpText = getFormValue(nodeId, formParams, questionLanguage, 'help_text', form);
          break;
      }

      return values;
    } else {
      const values = {
        isEnabled: { isDefault: true, value: form.enabled[questionLanguage] },
        isMandatory: { isDefault: true, value: form.is_mandatory[questionLanguage] },
        label: { isDefault: true, value: form.label[questionLanguage] },
        labelLeft: { isDefault: true, value: '' } as TIsDefaultValue,
        labelRight: { isDefault: true, value: '' } as TIsDefaultValue,
        unit: { isDefault: true, value: '' } as TIsDefaultValue,
        placeholder: { isDefault: true, value: '' } as TIsDefaultValue,
        helpText: { isDefault: true, value: '' } as TIsDefaultValue,
        min: { isDefault: true, value: 0 } as TIsDefaultValue,
        max: { isDefault: true, value: 10 } as TIsDefaultValue,
        step: { isDefault: true, value: 1 } as TIsDefaultValue,
      } satisfies TFormValuesResult;

      switch (form.type) {
        case EFormType.Phone:
        case EFormType.Text:
        case EFormType.Email:
          values.placeholder = { isDefault: true, value: form.placeholder[questionLanguage] };
          break;
        case EFormType.Number:
          values.min = { isDefault: true, value: form.min[questionLanguage] };
          values.max = { isDefault: true, value: form.max[questionLanguage] };
          values.step = { isDefault: true, value: form.step[questionLanguage] };
          values.placeholder = {
            isDefault: true,
            value:
              form.placeholder && questionLanguage in form.placeholder && form.placeholder[questionLanguage]
                ? form.placeholder[questionLanguage]
                : '',
          };
          break;
        case EFormType.Slider:
        case EFormType.Range:
          values.min = { isDefault: true, value: form.min[questionLanguage] };
          values.max = { isDefault: true, value: form.max[questionLanguage] };
          values.step = { isDefault: true, value: form.step[questionLanguage] };
          values.labelLeft = { isDefault: true, value: form.label_left[questionLanguage] };
          values.labelRight = { isDefault: true, value: form.label_right[questionLanguage] };
          values.unit = { isDefault: true, value: form.unit[questionLanguage] };
          break;
        case EFormType.Matrix:
          values.helpText = { isDefault: true, value: form.help_text[questionLanguage] };
          break;
      }

      return values;
    }
  }, [nodeId, questionLanguage, type, form, formId, nodeParams, templateId, mode]);

  return formValues || getDefaultFormValues();
}
